import React, {useEffect, useMemo, useState} from 'react';
import SchoolSetupForm from '../../SchoolManagement/Setup/SchoolSetupForm';
import AdmicityTabs from '../../../shared-components/AdmicityTabs';
import MisIntegration from './MisIntegration/MisIntegration';
import {Box} from '@mui/material';
import {useGetSchoolQuery, useUpdateSchoolMutation} from '../../../api/services/schoolManagementService';
import useUser from '../../../utility/hooks/useUser';
import AccessSettings from './AccessSettings/ManageAccessTab';
import FormCategories from './FormCategories/FormCategories';
import {getSchoolLogo, useDeleteLogoMutation, useUploadLogoMutation} from '../../../api/services/filesService';
import useNotification from '../../../utility/hooks/useNotification';
import ManageUsersTab from './ManageUsersTab';
import {SETTINGS_TAB_SX} from './SettingsPills';
import SyncLog from './SyncLog/SyncLog';
import LookupsTabs from './Lookups/LookupsTabs';
import GroupFilters from './GroupFilters/GroupFilters';
import EnquirySubmissions from './Enquiries/EnquirySubmissions';
import PaymentsSettings from './Payments/PaymentsSettings';
import {useSearchParams} from 'react-router-dom';

const USER_PART_FROM_LINK = {
    users: 'All Users',
    'user invites': 'User Invitations',
    'parent invitations': 'Parent Invitations',
    'parent invites': 'Parent Invitations'
};

const SchoolSettings = () => {
    const {user} = useUser();
    const [searchParams] = useSearchParams();
    // Resolved once, from ?tab=, so Stripe can send an administrator back to the tab they left from.
    // After that the tab is theirs to change and a background refetch must not pull them off it.
    const [activeTab, setActiveTab] = useState(null);
    const {
        data: schoolInfo = {
            name: '',
            dfeNumber: '',
            notes: '',
            domain: ''
        },
        isError,
        isFetching: isFetchingSchoolData,
        isLoading: isLoadingSchoolData,
        refetch: refetchSchoolInfo
    } = useGetSchoolQuery(user.schoolId, {skip: !user.schoolId});
    const [saveSchool, {isLoading: isSavingSchoolSettings}] = useUpdateSchoolMutation();
    const [uploadLogo, {isLoading: isSavingSchoolLogo}] = useUploadLogoMutation();
    const [deleteLogo, {isLoading: isDeletingSchoolLogo}] = useDeleteLogoMutation();
    const [schoolLogo, setSchoolLogo] = useState(null);
    const [isLoadingSchoolLogo, setIsLoadingSchoolLogo] = useState(false);
    const [fetchSchoolLogo, setFetchSchoolLogo] = useState(true);
    const {showErrorNotification, showSuccessNotification, showDetailedErrorNotification} = useNotification();

    useEffect(() => {
        if (isError) {
            showErrorNotification('Failed retrieve school information');
        }
    }, [isError]);

    useEffect(() => {
        if (schoolInfo?.id && fetchSchoolLogo) {
            setIsLoadingSchoolLogo(true);
            getSchoolLogo(schoolInfo.id)
                .then(r => {
                    setSchoolLogo(r.blob);
                    setFetchSchoolLogo(false);
                })
                .finally(() => setIsLoadingSchoolLogo(false));
        }
    }, [schoolInfo, fetchSchoolLogo]);

    const handleSaveForm = async (payload) => {
        const {logo, ...rest} = payload;

        try {
            await saveSchool(rest).unwrap();
        } catch (e) {
            if (e.data.validationErrors) {
                showDetailedErrorNotification('Failed to save school information', e.data.validationErrors);
            } else {
                showErrorNotification('Something went wrong while saving the school information');
            }

            return;
        }

        try {
            if (!logo) {
                if (schoolLogo) {
                    await deleteLogo().unwrap();
                    setSchoolLogo(null);
                }
            } else {
                if (!schoolLogo || schoolLogo.size !== logo.size || schoolLogo.type !== logo.type) {
                    await uploadLogo(logo).unwrap();
                    setFetchSchoolLogo(true)
                }
            }

            showSuccessNotification('School information saved successfully');
            refetchSchoolInfo();
        } catch (e) {
            showErrorNotification('Something went wrong while saving the school logo');
        }
    };

    // Maria, 26 October 2026: each tab's hover says only what the tab is for, in a few words
    // ('View the sync log and run manual syncs')
    const tabs = useMemo(() => [
        {
            // Maria, 20 October 2026: renamed from General; a link with the old name still opens the tab
            label: 'School Setup',
            hover: "Set up your school's main Admicity settings",
            aliases: ['General'],
            component: <SchoolSetupForm
                editMode
                schoolInfo={schoolInfo}
                logo={schoolLogo}
                onSubmit={handleSaveForm}
                isLoading={isLoadingSchoolData ||
                    isFetchingSchoolData ||
                    isLoadingSchoolLogo ||
                    isSavingSchoolSettings ||
                    isSavingSchoolLogo ||
                    isDeletingSchoolLogo}
            />
        },
        {
            label: 'MIS Integration',
            // Maria, 20 October 2026: the hover also names the SIMS Communications Permissions on this tab
            hover: 'Connect Admicity to SIMS',
            component: <MisIntegration/>
        },
        {
            // Maria, 20 October 2026: Users, User invites and Parent Invitations in one tab, with a button for each.
            // A link with an old tab name opens Manage Users at that button.
            label: 'Manage Users',
            hover: 'View user accounts and manage staff and parent invitations',
            aliases: Object.keys(USER_PART_FROM_LINK),
            component: <ManageUsersTab
                schoolInfo={schoolInfo}
                initialPart={USER_PART_FROM_LINK[(searchParams.get('tab') ?? '').toLowerCase()]}
            />
        },
        {
            label: 'Manage access',
            hover: 'Set View, Edit or Hide for each role on each profile section',
            component: <AccessSettings/>
        },
        {
            label: 'Categories',
            hover: 'Create categories to organise forms and groups',
            component: <FormCategories/>
        },
        {
            label: 'Sync logs',
            hover: 'View the sync log and run manual syncs',
            component: <SyncLog/>
        },
        {
            label: 'Lookups',
            hover: 'Edit the lookup lists and their introductory texts',
            component: <LookupsTabs/>
        },
        {
            // Maria, 20 October 2026: a shorter name; a link with the old name still opens the tab
            label: 'Filters',
            hover: 'Hide the filter groups your school does not need',
            aliases: ['Group Filters'],
            component: <GroupFilters schoolInfo={schoolInfo}/>
        },
        !schoolInfo.disableEnquiriesSync && {
            label: 'Enquiries',
            hover: 'View enquiries and generate the SIMS import file',
            component: <EnquirySubmissions/>
        },
        {
            label: 'Payments',
            hover: "Connect your school's Stripe account",
            component: <PaymentsSettings/>
        },
    ].filter(Boolean), [
        schoolInfo,
        schoolLogo,
        isFetchingSchoolData,
        isLoadingSchoolData,
        isLoadingSchoolLogo,
        isSavingSchoolSettings,
        isSavingSchoolLogo,
        isDeletingSchoolLogo
    ]);

    useEffect(() => {
        if (activeTab !== null) return;

        const requested = searchParams.get('tab');
        const index = requested
            ? tabs.findIndex(tab => [tab.label, ...(tab.aliases ?? [])]
                .some(name => name?.toLowerCase() === requested.toLowerCase()))
            : -1;

        setActiveTab(index >= 0 ? index : 0);
    }, [activeTab, searchParams, tabs]);

    return (
        <Box
            display={'flex'}
            flexDirection={'column'}
            flexGrow={1}
            minHeight={'100%'}
        >
            <AdmicityTabs
                // Maria, 20 October 2026: the tab names a little closer together
                // Maria, 20 October 2026: and spread evenly across the page
                // AD-2737: only on a large screen; on a smaller one the row scrolls (SETTINGS_TAB_SX)
                tabs={tabs.map(tab => ({...tab, tabSx: SETTINGS_TAB_SX}))}
                variant={'scrollable'}
                tabIndex={activeTab ?? 0}
                onChange={setActiveTab}
            />
        </Box>
    );
};

export default SchoolSettings;